import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { Store } from '@ngxs/store';
import { MentionMeService } from '@x/common/referral/mention-me/services/mention-me.service';
import { AuthLogout } from '@x/ecommerce-shop/app/core/auth/state/auth.actions';
import { IShopAuthUser } from '@x/ecommerce/shop-client';

interface MyAccountMenuItem {
  path: string;
  label: string;
  icon: IconName;
  condition?: () => boolean;
  action?: () => void;
}

@Component({
  selector: 'x-my-account-menu',
  templateUrl: './my-account-menu.component.html',
  styleUrls: ['./my-account-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.x-my-account-menu]': '!isMobile',
    '[class.x-my-account-menu-mobile]': 'isMobile',
  },
})
export class MyAccountMenuComponent implements OnInit {
  @Input() isMobile = false;
  @Input() user: IShopAuthUser | null;

  menuItems: MyAccountMenuItem[] = [];

  get channelCode() {
    return this.route.snapshot.params.channelCode;
  }

  constructor(
    public route: ActivatedRoute,
    private store: Store,
    private router: Router,
    public mentionMeService: MentionMeService,
  ) {}

  ngOnInit() {
    this.initMenuItems();
  }

  initMenuItems() {
    this.menuItems = [
      {
        path: `/my-account/profile`,
        label: $localize`:@@MyAccountPages.Profile:Profile`,
        icon: 'user',
      },
      {
        path: `/my-account/order-history`,
        label: $localize`:@@MyAccountPages.OrderHistory:Order History`,
        icon: 'list',
      },
      {
        path: `/my-account/payment-credit`,
        label: $localize`:@@MyAccountPages.PaymentCredit:Payment & Credit`,
        icon: 'credit-card',
      },
      {
        path: `/my-account/address-book`,
        label: $localize`:@@MyAccountPages.AddressBook:Address Book`,
        icon: 'map-pin',
      },
      {
        path: `/my-account/subscriptions`,
        label: $localize`:@@MyAccountPages.Subscriptions:Subscriptions`,
        icon: 'arrows-rotate',
      },
      {
        path: `/my-account/memberships`,
        label: $localize`:@@MyAccountPages.Memberships:Memberships`,
        icon: 'clipboard-list',
      },
      {
        path: `/my-account/vouchers`,
        label: $localize`:@@MyAccountPages.Vouchers:Vouchers`,
        icon: 'gift-card',
      },
      {
        path: `/my-account/favourites`,
        label: $localize`:@@MyAccountPages.Favourites:Favourites`,
        icon: 'heart-broken',
      },
      {
        path: `/my-account/referral`,
        label: $localize`:@@MyAccountPages.ReferralDashboard:Referral Dashboard`,
        icon: 'clipboard-list',
        condition: () => this.mentionMeService.isConfigured,
      },
      {
        path: '',
        label: $localize`:@@CallToAction.Logout:Logout`,
        icon: 'sign-out',
        action: () => this.logout(),
      },
    ];
  }

  getRouterLink(item: MyAccountMenuItem): string | null {
    if (item.action) {
      return null;
    }
    return '/' + this.channelCode + item.path;
  }

  handleItemClick(item: MyAccountMenuItem): void {
    if (item.action) {
      item.action();
    }
  }

  logout() {
    this.store.dispatch(new AuthLogout());

    if (this.router.url.includes('my-account')) {
      this.router.navigate([`/${this.channelCode}`]);
    }
  }
}
